import type { ReactNode } from 'react';
import { Button } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { IntakeSource, VolumeBreakdown } from '../types';
import { SortableTable, type SortRow } from './SortableTable';
import type { Span } from './spans';
import './insights.css';

/** How pages came in, in the words the product uses. */
export const intakeWord: Record<IntakeSource, string> = {
  upload: 'Uploaded',
  email: 'Email',
  folder: 'Watched folder',
  scanner: 'Paige Scan',
  api: 'API',
  demo: 'Sample pages',
};

/**
 * A share as a bar left-anchored in a fixed track, then its percent (round 2, dataviz S2): 73% and
 * 22% start at the same x in every row of both tables, so the lengths compare.
 */
function Share({ value, total, f }: { value: number; total: number; f: Formatter }): ReactNode {
  const share = total > 0 ? value / total : 0;
  return (
    <span className="ad-share">
      <span className="ad-share__track" aria-hidden="true">
        <span className="ad-share__bar" style={{ inlineSize: `${(share * 100).toFixed(1)}%` }} />
      </span>
      <span className="ad-share__value">{f.percent(share * 100, 0)}</span>
    </span>
  );
}

function rowsOf(
  items: readonly { key: string; name: string; pages: number; documents: number }[],
  f: Formatter,
): SortRow[] {
  const total = items.reduce((s, x) => s + x.pages, 0);
  return items.map((x) => ({
    key: x.key,
    cells: [
      <span key="n" title={x.name}>
        {x.name}
      </span>,
      f.int(x.pages),
      f.int(x.documents),
      <Share key="s" value={x.pages} total={total} f={f} />,
    ],
    values: [x.name, x.pages, x.documents, x.pages],
  }));
}

const COLUMNS = (first: string) => [
  { heading: first },
  { heading: 'Pages', numeric: true },
  { heading: 'Documents', numeric: true },
  { heading: 'Share', numeric: true },
];

export interface BreakdownPanelProps {
  span: Span;
  breakdown: VolumeBreakdown | undefined;
  failed: boolean;
  onClose: () => void;
}

function Tables({ breakdown, f }: { breakdown: VolumeBreakdown; f: Formatter }): ReactNode {
  const jobs = breakdown.jobs.map((j) => ({ ...j, key: j.jobId }));
  const sources = breakdown.sources.map((x) => ({
    ...x,
    key: x.source,
    name: intakeWord[x.source],
  }));
  return (
    <div className="ad-breakdown__tables">
      <SortableTable
        caption="Pages by job"
        columns={COLUMNS('Job')}
        initial={BIGGEST}
        rows={rowsOf(jobs, f)}
      />
      <SortableTable
        caption="Pages by how they came in"
        columns={COLUMNS('Came in by')}
        initial={BIGGEST}
        rows={rowsOf(sources, f)}
      />
    </div>
  );
}

const BIGGEST = { by: 1, descending: true };

/** The detail behind a picked column: which jobs and which doors the pages came through. */
export function BreakdownPanel({
  span,
  breakdown,
  failed,
  onClose,
}: BreakdownPanelProps): ReactNode {
  const f = useFormat();
  const pages = breakdown?.sources.reduce((s, x) => s + x.pages, 0) ?? 0;
  const status = breakdown
    ? `${f.int(pages)} pages`
    : failed
      ? 'Could not load the detail'
      : 'Loading';
  return (
    <section className="ad-breakdown" aria-live="polite" aria-label={`Detail for ${span.title}`}>
      <header className="ad-breakdown__head">
        <strong>{span.title}</strong>
        <span className="ad-small">{status}</span>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </header>
      {breakdown && pages === 0 ? <p className="ad-vol__empty">No pages on these days.</p> : null}
      {breakdown && pages > 0 ? <Tables breakdown={breakdown} f={f} /> : null}
    </section>
  );
}
